"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

/* ------------------------------------------------------------------ */
/*  Regex and constants at module level (Biome rule)                  */
/* ------------------------------------------------------------------ */

const RE_SPLIT_WORDS = /\s+/;

const SKIP_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "but",
  "of",
  "in",
  "on",
  "at",
  "to",
  "for",
  "with",
  "by",
  "from",
  "is",
  "it",
  "as",
]);

// Word bank for acronym expansion (organized by starting letter)
const WORD_BANK: Record<string, string[]> = {
  A: [
    "Advanced",
    "Automated",
    "Adaptive",
    "Analytical",
    "Applied",
    "Active",
    "Academic",
  ],
  B: [
    "Based",
    "Behavioral",
    "Breakthrough",
    "Building",
    "Broad",
    "Balanced",
    "Basic",
  ],
  C: [
    "Collaborative",
    "Comprehensive",
    "Critical",
    "Creative",
    "Core",
    "Central",
    "Connected",
  ],
  D: [
    "Digital",
    "Dynamic",
    "Data",
    "Development",
    "Distributed",
    "Designed",
    "Direct",
  ],
  E: [
    "Enhanced",
    "Efficient",
    "Emerging",
    "Evaluation",
    "Educational",
    "Enabled",
    "Enterprise",
  ],
  F: [
    "Framework",
    "Flexible",
    "Focused",
    "Functional",
    "Future",
    "Facilitated",
    "Foundational",
  ],
  G: [
    "Global",
    "Guided",
    "Generative",
    "Growth",
    "Governance",
    "Geographic",
    "General",
  ],
  H: [
    "Hybrid",
    "High-performance",
    "Holistic",
    "Hub",
    "Health",
    "Harmonized",
    "Human",
  ],
  I: [
    "Integrated",
    "Intelligent",
    "Interactive",
    "Innovative",
    "Infrastructure",
    "Impact",
    "Inclusive",
  ],
  J: [
    "Joint",
    "Justified",
    "Judicious",
    "Jurisdictional",
    "Journey",
    "Junior",
    "Juncture",
  ],
  K: ["Knowledge", "Key", "Kinetic", "Kernel", "Keen", "Kit", "Known"],
  L: [
    "Learning",
    "Linked",
    "Logical",
    "Leveraged",
    "Leadership",
    "Layered",
    "Local",
  ],
  M: [
    "Managed",
    "Modular",
    "Metrics",
    "Multi-level",
    "Machine",
    "Mobile",
    "Methodical",
  ],
  N: [
    "Network",
    "Next-generation",
    "Novel",
    "Normalized",
    "National",
    "Natural",
    "Navigated",
  ],
  O: [
    "Optimized",
    "Open",
    "Operational",
    "Organized",
    "Outcome",
    "Online",
    "Orchestrated",
  ],
  P: [
    "Platform",
    "Predictive",
    "Progressive",
    "Proactive",
    "Protocol",
    "Performance",
    "Practical",
  ],
  Q: [
    "Quality",
    "Quantitative",
    "Quick",
    "Qualified",
    "Quantum",
    "Queried",
    "Quota",
  ],
  R: [
    "Research",
    "Responsive",
    "Robust",
    "Real-time",
    "Resource",
    "Resilient",
    "Reliable",
  ],
  S: [
    "System",
    "Strategic",
    "Scalable",
    "Sustainable",
    "Smart",
    "Structured",
    "Solutions",
  ],
  T: [
    "Technology",
    "Transformative",
    "Targeted",
    "Transparent",
    "Toolkit",
    "Tracked",
    "Total",
  ],
  U: [
    "Unified",
    "Universal",
    "User-centered",
    "Utility",
    "Upstream",
    "Updated",
    "Unique",
  ],
  V: [
    "Virtual",
    "Validated",
    "Value-driven",
    "Versatile",
    "Visible",
    "Volumetric",
    "Verified",
  ],
  W: [
    "Web",
    "Workflow",
    "Workspace",
    "Wireless",
    "Wide",
    "Well-defined",
    "Weighted",
  ],
  X: [
    "Extended",
    "Cross-platform",
    "Experimental",
    "Executive",
    "External",
    "Expert",
    "Explored",
  ],
  Y: ["Yield", "Year-round", "Youth", "Yearly", "Yearned", "Yellow", "Yoked"],
  Z: ["Zero-trust", "Zone", "Zonal", "Zenith", "Zoned", "Zealous", "Zipped"],
};

type Mode = "create" | "expand";

interface AcronymResult {
  acronym: string;
  label: string;
}

interface ExpansionResult {
  expansion: string;
  index: number;
}

function createAcronyms(phrase: string, strings: Strings): AcronymResult[] {
  const words = phrase.trim().split(RE_SPLIT_WORDS).filter(Boolean);
  if (words.length === 0) {
    return [];
  }

  const results: AcronymResult[] = [];

  // Primary: first letter of all words
  const allFirst = words.map((w) => w[0].toUpperCase()).join("");
  results.push({ acronym: allFirst, label: strings.allWords });

  // Skip minor words
  const significant = words.filter((w) => !SKIP_WORDS.has(w.toLowerCase()));
  if (significant.length > 0 && significant.length !== words.length) {
    const sigFirst = significant.map((w) => w[0].toUpperCase()).join("");
    results.push({ acronym: sigFirst, label: strings.significantWordsOnly });
  }

  // First two letters of each significant word
  if (significant.length >= 2 && significant.length <= 4) {
    const twoLetters = significant
      .map((w) => w.slice(0, 2).toUpperCase())
      .join("");
    results.push({ acronym: twoLetters, label: strings.firstTwoLettersEach });
  }

  // Consonants only from first letters + vowel from first word
  if (words.length >= 3) {
    const firstWord = words[0];
    const consonants = words
      .slice(1)
      .map((w) => w[0].toUpperCase())
      .filter((c) => !"AEIOU".includes(c));
    if (consonants.length > 0) {
      const mixed = firstWord[0].toUpperCase() + consonants.join("");
      results.push({
        acronym: mixed,
        label: strings.firstWordConsonantInitials,
      });
    }
  }

  return results;
}

function expandAcronym(acronym: string): ExpansionResult[] {
  const letters = acronym
    .toUpperCase()
    .split("")
    .filter((c) => c >= "A" && c <= "Z");
  if (letters.length === 0) {
    return [];
  }

  const results: ExpansionResult[] = [];

  // Generate 3 variations
  for (let v = 0; v < 3; v++) {
    const words: string[] = [];
    for (const letter of letters) {
      const options = WORD_BANK[letter] || [`[${letter}...]`];
      const idx = v % options.length;
      words.push(options[idx]);
    }
    results.push({ expansion: words.join(" "), index: v });
  }

  return results;
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const ACRONYM_GENERATOR_STRINGS = {
  createAcronym: "Create Acronym",
  expandAcronym: "Expand Acronym",
  enterAPhraseOrSet: "Enter a phrase or set of words",
  enterAnAcronymToExpand: "Enter an acronym to expand",
  generateAcronyms: "Generate Acronyms",
  generatedAcronyms: "Generated Acronyms",
  copied: "Copied!",
  copy: "Copy",
  possibleExpansionsFor: "Possible Expansions for",
  allWords: "All words",
  significantWordsOnly: "Significant words only",
  firstTwoLettersEach: "First two letters each",
  firstWordConsonantInitials: "First word + consonant initials",
  phrasePlaceholder: "e.g., Sustainable Energy Research Initiative",
  acronymPlaceholder: "e.g., SERI",
} as const;

type Strings = typeof ACRONYM_GENERATOR_STRINGS;

export default function AcronymGeneratorWidget() {
  const strings = useToolUi(ACRONYM_GENERATOR_STRINGS);
  const [mode, setMode] = useState<Mode>("create");
  const [input, setInput] = useState("");
  const [acronyms, setAcronyms] = useState<AcronymResult[] | null>(null);
  const [expansions, setExpansions] = useState<ExpansionResult[] | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  function handleGenerate() {
    setCopied(null);
    if (mode === "create") {
      setAcronyms(createAcronyms(input, strings));
      setExpansions(null);
    } else {
      setExpansions(expandAcronym(input));
      setAcronyms(null);
    }
  }

  async function handleCopy(key: string, text: string) {
    if (!(await writeToClipboard(text))) {
      return;
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <>
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <button
          className={`rounded-lg px-4 py-2 font-medium text-sm transition-colors ${mode === "create" ? "bg-primary text-primary-foreground" : "border border-border bg-card text-muted-foreground hover:text-foreground"}`}
          onClick={() => {
            setMode("create");
            setAcronyms(null);
            setExpansions(null);
            setInput("");
          }}
          type="button"
        >
          {strings.createAcronym}
        </button>
        <button
          className={`rounded-lg px-4 py-2 font-medium text-sm transition-colors ${mode === "expand" ? "bg-primary text-primary-foreground" : "border border-border bg-card text-muted-foreground hover:text-foreground"}`}
          onClick={() => {
            setMode("expand");
            setAcronyms(null);
            setExpansions(null);
            setInput("");
          }}
          type="button"
        >
          {strings.expandAcronym}
        </button>
      </div>
      {/* Input */}
      <div className="space-y-4 rounded-lg border border-border bg-card p-6">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ag-input"
          >
            {mode === "create"
              ? strings.enterAPhraseOrSet
              : strings.enterAnAcronymToExpand}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="ag-input"
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              mode === "create"
                ? strings.phrasePlaceholder
                : strings.acronymPlaceholder
            }
            type="text"
            value={input}
          />
        </div>
        <button
          className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-colors hover:bg-primary/90"
          onClick={handleGenerate}
          type="button"
        >
          {mode === "create" ? strings.generateAcronyms : strings.expandAcronym}
        </button>
      </div>
      {/* Create Results */}
      {acronyms && acronyms.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.generatedAcronyms}
          </h2>
          <div className="space-y-3">
            {acronyms.map((r) => (
              <div
                className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4"
                key={`${r.acronym}-${r.label}`}
              >
                <div>
                  <p className="font-bold text-2xl text-foreground tracking-wider">
                    {r.acronym}
                  </p>
                  <p className="mt-1 text-muted-foreground text-xs">
                    {r.label}
                  </p>
                </div>
                <button
                  className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
                  onClick={() => handleCopy(r.acronym, r.acronym)}
                  type="button"
                >
                  {copied === r.acronym ? strings.copied : strings.copy}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* Expand Results */}
      {expansions && expansions.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.possibleExpansionsFor}{" "}
            <span className="font-bold tracking-wider">
              {input.toUpperCase()}
            </span>
          </h2>
          <div className="space-y-3">
            {expansions.map((r) => (
              <div
                className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4"
                key={`e-${r.index}`}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  {r.expansion}
                </p>
                <button
                  className="shrink-0 rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
                  onClick={() => handleCopy(`exp-${r.index}`, r.expansion)}
                  type="button"
                >
                  {copied === `exp-${r.index}` ? strings.copied : strings.copy}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
